<template>
	<div :class="{'container-fluid': !isSettingsComponent}">
		<settings-toolbar v-if="!isSettingsComponent" title="Company Details" icon="fa-pencil-alt"></settings-toolbar>

		<onboarding-card title="Add your company details & logo" name="Company Details" setting-key="companyDetails" next-link="/time-logs/settings">
			<p>This is your company details page, fill out the rest of your company's details. Add a logo, which is what will also be used for documents like invoices and purchase orders.</p>

			<p>When you're happy, click the button below and we'll move onto what's next.</p>
		</onboarding-card>

		<div class="row">
			<div class="col-md-3 text-center">
				<label>Logo</label>

				<image-panel
					:image-url="$store.companyImageUrl"
					icon="fa-building"
					v-on:deleteImg="deleteProfileImage"
					v-on:rotate="rotateImage">
				</image-panel>

				<upload-field 
					upload-msg="Add Logo..." 
					target="ProfileImage/save"
					style-type="link"
					v-on:uploaded="imageUploaded">
				</upload-field>

				<widget v-if="referralLink" title="Your Referral Link" class="mt-4">
					<p>Share this link with your friends and take part in our referral rewards program!</p>

					<div class="d-flex flex-row">

						<div style="flex-grow: 1">
							<field
								label="Share Link"
								col-class="stacked"
								:value="referralLink">
							</field>
						</div>
						<div style="align-self: flex-end;margin-bottom: 0.3rem">
							<button class="btn btn-light btn-attached" type="button" title="Copy link to clipboard" v-on:click="copyToClipboard(referralLink)">
								<i class="fa fa-copy"></i>
							</button>
						</div>

					</div>
				</widget>
			</div>
			<div class="col-md-9">

				<vue-form v-on:submit="saveDetails" size="xsm">
					
					<section>
						<h3>Company Details</h3>

						<field label="Company Name" 
							col-class="col-md-4" 
							v-model="$store.company.company_name">
						</field>

						<field label="Website" col-class="col-md-4" v-model="$store.company.website"></field>

						<field label="Business Type" 
							col-class="col-md-4" 
							v-model="$store.company.BusinessType"
							:params="{type:'select-multi',options:businessTypeOptions}">
						</field>
						
						<field label="Main Phone #" col-class="col-md-4" v-model="$store.company.phone"></field>
						<field label="Main Email" col-class="col-md-4" v-model="$store.company.email"></field>

						<field :readonly="$store.company.managing_user_id != $store.userDetails.id && $store.company.managing_user_id != 0" label="Managing User" 
							col-class="col-md-4" 
							v-model="companyHeadId" 
							:params="{type:'select',options:userOptions()}">

							<span class="text-muted">Typically the head of the company, or the highest ranked employee who administrates {{$store.appNameProper}}</span>

						</field>

						<field :readonly="$store.company.secondary_contact_id != $store.userDetails.id && $store.company.secondary_contact_id != 0" label="Secondary Contact" 
							col-class="col-md-4" 
							v-model="$store.company.secondary_contact_id" 
							:params="{type:'select',options:userOptions()}">

							<span class="text-muted">Secondary contact for your company</span>

						</field>

						<field label="Safety Officer" 
							col-class="col-md-4" 
							v-model="$store.company.safety_officer_id" 
							:params="{type:'select',options:userOptions()}">

							<span class="text-muted">The Safety Officer will be notified of all incompleted safety forms.</span>

						</field>

						<field label="Equipment Manager" 
							col-class="col-md-4" 
							v-model="$store.company.equipment_manager_id" 
							:params="{type:'select',options:userOptions()}">

							<span class="text-muted">The Equipment Manager will be notified of all equipment related events.</span>

						</field>

						<field label="Accounting Manager" 
							col-class="col-md-4" 
							v-model="$store.company.accounting_manager_id" 
							:params="{type:'select',options:userOptions()}">

							<span class="text-muted">The Accounting Manager will be notified of all accounting related events.</span>

						</field>

						<field :readonly="$store.company.marketing_manager_id != $store.userDetails.id && $store.company.marketing_manager_id != 0" label="Marketing Manager" 
							col-class="col-md-4" 
							v-model="$store.company.marketing_manager_id" 
							:params="{type:'select',options:userOptions()}">

						</field>

					</section>

					<section>
						<h3>Head Office Address</h3>

						<address-manual-selector :hide-suite="true"
							:address="$store.company.address"
							:city="$store.company.city"
							:province="$store.company.province"
							:country="$store.company.country"
							:postal-code="$store.company.postal_code"
							col-class="col-md-4"
							v-on:input="updateAddress">
						</address-manual-selector>

					</section>

					<field label="Has Separate Billing Address" col-class="col-md-6"
						v-model="$store.company.has_billing_address"
						:params="{type:'boolean'}">
					</field>

					<section v-if="$store.company.has_billing_address == 1">
						<h3>Billing Address</h3>

						<address-manual-selector :hide-suite="true"
							:address="$store.company.billing_address"
							:city="$store.company.billing_city"
							:province="$store.company.billing_province"
							:country="$store.company.billing_country"
							:postal-code="$store.company.billing_postal_code"
							col-class="col-md-4"
							v-on:input="updateAddress($event,'billing_')">
						</address-manual-selector>

					</section>

					<section>
						<h3>Public Company Description</h3>
						<p>This description will tell people what your business does.</p>

						<field v-model="$store.company.description" :params="{type:'textarea'}"></field>
					</section>
				</vue-form>

			</div>
		</div>

	</div>
</template>

<script>
import SettingsToolbar from "./SettingsToolbar"

import VueForm from "@/components/VueForm"
import Field from "@/components/Field"
import UploadField from "@/components/UploadField"
import ImagePanel from "@/components/ImagePanel"

import Widget from "@/components/Widget"

import ModalDialog from "@/components/ModalDialog"
import OnboardingCard from "@/components/OnboardingCard"
import AddressManualSelector from "@/components/AddressManualSelector"

export default {

	name: 'SettingsCompanyDetails',
	components: {
		SettingsToolbar,
		VueForm,
		Field,
		ImagePanel,
		UploadField,
		ModalDialog,
		OnboardingCard,
		AddressManualSelector,
		Widget
	},
	props: ["isSettingsComponent"],
	data() {
		return {
			payload: {},
			companyHeadId: null,
			companyTypes: []
		}
	},
	computed: {
		businessTypeOptions() {
			let vm = this

			return this.optionify(this.companyTypes,"type")
		},
		referralLink() {
			if (this.$store.company && 'company_name' in this.$store.company)
				return "https://tradetraks.ca?referral="+this.$store.company.company_name.slugify()
			return ""
		}
	},
	methods: {
		updateAddress(address,prefix) {
			let vm = this

			if (!prefix)
				prefix = ""

			Object.keys(address).forEach((key,index) => {
				vm.$set(vm.$store.company,prefix+key,address[key])
			})
		},
		deleteProfileImage() {
			this.spinner(true);

			let vm = this

			let payload = {
				id: this.$store.company.image_id
			}

			Promise.all([
				this.postData("Company/save",this.$store.company),
				this.postData("ProfileImage/delete",payload)
			]).then(function(responses){
				vm.spinner(false)

				vm.$store.company.ProfileImage = null
				vm.$store.company.image_id = 0
			})
		},
		rotateImage(dir) {
			let payload = {
				id: this.$store.company.image_id,
				rotate_direction: dir
			}

			let vm = this

			this.postData("ProfileImage/rotate",payload).then(response => {
				//strip query string, and then add another random one

				let imageUrl = vm.$store.companyImageUrl.split("?")[0]

				vm.$store.companyImageUrl = imageUrl+"?"+Math.random()

				vm.$forceUpdate()
			})
		},
		imageUploaded(response) {

			this.$store.companyImageUrl = this.$root.apiRoot+'resources/profile-images/'+response.data.slug+'/'+response.data.filename

			this.$store.company.image_id = response.data.id

			this.saveDetails()
		},
		saveDetails() {
			let vm = this

			let payload = JSON.parse(JSON.stringify(this.$store.company))
			payload.managing_user_id = this.companyHeadId
			delete payload.ProfileImage
			delete payload.CompanySetting

			this.saveRecord("Company",payload,response => {

				vm.$store.company.managing_user_id = payload.managing_user_id
				vm.companyHeadId = vm.$store.company.managing_user_id

				vm.alertSaved("Details saved")
			})
		},
		fetchData() {
			let vm = this

			let promises = [
				this.fetchUsers(false, false, false, ['EmployeeSetting']),
				this.fetchCompanyData(true,["ProfileImage","BusinessType","CompanySetting"]),
				this.getJSON("BusinessType/get"),
			]

			Promise.all(promises).then(responses => {
				//vm.$store.company = responses[1].data
				vm.companyHeadId = responses[1].data.managing_user_id
				vm.companyTypes = responses[2].data
				vm.$store.company.BusinessType = vm.primitize(responses[1].data.BusinessType)
			})
		}
	},
	created() {
		if ("companySetup" in localStorage && localStorage.companySetup == 'true')
			this.$store.companySetupMode = true

		this.fetchData()
	}
}
</script>